import { useState } from 'react';
import { VairUser } from 'constants/payloads/vair/playerSearch';
import { GetUserProfileByNameSearchQuery } from 'types/generated/client';
import { parseVairSportRating } from 'utils/shared/vair/parsePrimaryRating';
import { Button } from 'components/Button/Button';
import { useModal } from 'components/modals/Modal';
import Header from './Header';
import ModalInvitePartner from './ModalInvitePartner';
import ModalSearchPartner from './ModalSearchPartner';
import type { UserNeedingPartner } from './UserEventList/utils';

type DatabaseUser = GetUserProfileByNameSearchQuery['userProfiles'][number];

export interface PartnerSetupGroup {
  groupId: string;
  groupTitle: string;
  usersNeedingPartner: UserNeedingPartner[];
}

interface SelectedPartner {
  name: string;
  userId?: string | null;
  vairRating?: string | null;
}

interface Props {
  groups: PartnerSetupGroup[];
  vairUsersMap?: Record<string, VairUser>;
  initialSelectedPartners?: Record<string, SelectedPartner>;
  onBack: () => void;
  onPartnerSelected: (groupId: string, user: UserNeedingPartner) => void;
  onPartnerInvited: (groupId: string, email: string) => void;
  onDatabaseUserInvited: (groupId: string, user: DatabaseUser) => void;
  onNext: () => void;
}

export default function PartnerSetup({
  groups,
  vairUsersMap,
  initialSelectedPartners,
  onBack,
  onPartnerSelected,
  onPartnerInvited,
  onDatabaseUserInvited,
  onNext,
}: Props) {
  const {
    isOpen: isSearchOpen,
    openModal: openSearchModal,
    closeModal: closeSearchModal,
  } = useModal();
  const {
    isOpen: isInviteOpen,
    openModal: openInviteModal,
    closeModal: closeInviteModal,
  } = useModal();

  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const [selectedPartners, setSelectedPartners] = useState<Record<string, SelectedPartner>>(
    initialSelectedPartners ?? {},
  );

  const activeGroup = groups.find((g) => g.groupId === activeGroupId);

  const handleOpenSearch = (groupId: string) => {
    setActiveGroupId(groupId);
    openSearchModal();
  };

  const handleOpenInvite = () => {
    closeSearchModal();
    openInviteModal();
  };

  return (
    <div className="tournament-register-form flex h-full flex-col">
      <div className="px-6 pt-6">
        <Header
          title="Partner setup"
          showBack
          showBackAction={onBack}
          cta="Select a partner"
          ctaClassName="!typography-product-body font-normal text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary"
        />
      </div>
      <div className="mt-6 h-px w-full bg-color-border-input-lightmode dark:bg-color-border-input-darkmode" />
      <div className="flex-auto overflow-y-auto px-6 pt-6">
        {groups.map((group, index) => {
          const partner = selectedPartners[group.groupId];
          return (
            <div key={group.groupId}>
              <p className="typography-product-heading-compact-desktop text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                {group.groupTitle}
              </p>
              {partner ? (
                <>
                  <p className="mt-2 text-sm text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                    {partner.name}
                    {partner.vairRating && (
                      <span className="ml-2 text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
                        VAIR {partner.vairRating}
                      </span>
                    )}
                  </p>
                  <Button
                    variant="secondary"
                    size="md"
                    className="mt-3 w-full"
                    onClick={() => handleOpenSearch(group.groupId)}
                  >
                    Change partner
                  </Button>
                </>
              ) : (
                <Button
                  variant="primary"
                  size="md"
                  className="mt-4 w-full"
                  onClick={() => handleOpenSearch(group.groupId)}
                >
                  Select partner
                </Button>
              )}
              {index < groups.length - 1 && (
                <div className="my-6 h-px w-full bg-color-border-input-lightmode dark:bg-color-border-input-darkmode" />
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-ds-xl h-px w-full bg-color-border-input-lightmode dark:bg-color-border-input-darkmode" />
      <div className="px-6 pb-6 pt-6">
        <Button variant="brand" size="lg" className="w-full" onClick={onNext}>
          Next
        </Button>
      </div>
      <div className="safearea-spacer-bot w-full" />

      <ModalSearchPartner
        isOpen={isSearchOpen}
        onClose={closeSearchModal}
        usersNeedingPartner={activeGroup?.usersNeedingPartner ?? []}
        vairUsersMap={vairUsersMap}
        initialSelectedUserId={activeGroupId ? selectedPartners[activeGroupId]?.userId : null}
        initialSelectedName={activeGroupId ? selectedPartners[activeGroupId]?.name : null}
        onSelectPartner={(user) => {
          if (!activeGroupId) return;
          const vairUser = user.userId ? vairUsersMap?.[user.userId] : undefined;
          const parsed = parseVairSportRating(vairUser?.sport?.pickleball);
          setSelectedPartners((prev) => ({
            ...prev,
            [activeGroupId]: {
              name: user.userProfile?.fullName || user.userProfile?.preferredName || '',
              userId: user.userId,
              vairRating: parsed.formattedValue,
            },
          }));
          onPartnerSelected(activeGroupId, user);
          closeSearchModal();
        }}
        onInvitePartner={handleOpenInvite}
        onInviteDatabaseUser={(user) => {
          if (!activeGroupId) return;
          setSelectedPartners((prev) => ({
            ...prev,
            [activeGroupId]: {
              name: user.fullName || user.username || '',
              userId: user.id,
            },
          }));
          onDatabaseUserInvited(activeGroupId, user);
          closeSearchModal();
        }}
      />

      <ModalInvitePartner
        isOpen={isInviteOpen}
        onClose={closeInviteModal}
        onInvite={(email) => {
          if (!activeGroupId) return;
          setSelectedPartners((prev) => ({
            ...prev,
            [activeGroupId]: {
              name: email,
            },
          }));
          onPartnerInvited(activeGroupId, email);
          closeInviteModal();
        }}
      />
    </div>
  );
}
